import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { format } from "date-fns";
import { hr } from "date-fns/locale";
import { Trash2, Heart } from "lucide-react";
import { useAuth } from "../hooks/use-auth";
import { cn } from "../lib/utils";
import type { Comment, User } from "../types";
import { toggleLikeComment, deleteComment } from "../lib/data";
import { useDoubleTap } from "../hooks/use-double-tap";
import { VideoProcessingPlaceholder } from "./video-player";
import { isVideoStillProcessing } from "../lib/video-upload";
import { StillSending } from "./still-sending";

interface CommentListProps {
    postId: string;
    comments: (Comment & { author: User })[];
}

export function CommentList({ postId, comments }: CommentListProps) {
    const { user } = useAuth();

    if (comments.length === 0) {
        return null;
    }

    return (
        <div className="space-y-4 mt-4">
            {comments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} postId={postId} currentUser={user} />
            ))}
        </div>
    );
}

function CommentItem({ comment, postId, currentUser }: { comment: Comment & { author: User }, postId: string, currentUser: User | null }) {
    const [isLiked, setIsLiked] = useState((comment.likedBy || []).includes(currentUser?.id || ""));
    const [likeCount, setLikeCount] = useState(comment.likes);
    const [showHeartAnimation, setShowHeartAnimation] = useState(false);

    const isAuthor = currentUser?.id === comment.authorId;
    const canDelete = currentUser && (isAuthor || ['admin', 'trainer', 'glavni_trener'].includes(currentUser.role));

    const handleLike = async () => {
        if (!currentUser) return;
        setIsLiked(prev => !prev);
        setLikeCount(prev => (isLiked ? prev - 1 : prev + 1));
        try {
            await toggleLikeComment(postId, comment.id, currentUser.id);
        } catch (error) {
            setIsLiked(prev => !prev);
            setLikeCount(prev => (isLiked ? prev + 1 : prev - 1));
        }
    };

    const handleDoubleTapLike = () => {
        if (!currentUser || isLiked) return;
        handleLike();
        setShowHeartAnimation(true);
        setTimeout(() => setShowHeartAnimation(false), 1000);
    };

    const handleDoubleTap = useDoubleTap({
        onDoubleTap: handleDoubleTapLike,
    });

    const handleDelete = async () => {
        if (!canDelete) return;
        if (confirm("Obriši komentar?")) {
            await deleteComment(postId, comment.id);
        }
    }

    return (
        <div className="flex gap-3 text-sm">
            <Avatar className="h-8 w-8">
                <AvatarImage src={comment.author.avatar} />
                <AvatarFallback>{comment.author.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
                <div className="relative">
                    <div
                        className="bg-muted p-3 rounded-lg cursor-pointer select-none"
                        onClick={handleDoubleTap}
                    >
                        <div className="flex justify-between items-start">
                            <span className="font-semibold">{comment.author.name}</span>
                            <span className="text-xs text-muted-foreground">{format(new Date(comment.createdAt), "d.M. HH:mm", { locale: hr })}</span>
                        </div>
                        <p className="whitespace-pre-wrap mt-1">{comment.content}</p>
                        <StillSending path={`posts/${postId}/comments/${comment.id}`} className="mt-1" />
                        {comment.mediaUrl && (
                            <div className="mt-2">
                                {comment.mediaType === 'video' && isVideoStillProcessing(comment) ? (
                                    <VideoProcessingPlaceholder className="max-h-48 max-w-xs rounded-md" />
                                ) : comment.mediaType === 'video' ? (
                                    <video src={comment.mediaUrl} poster={comment.thumbnailUrl} controls playsInline preload="metadata" className="max-h-48 rounded-md" />
                                ) : (
                                    <img src={comment.mediaUrl} alt="Comment media" className="max-h-48 rounded-md object-contain" />
                                )}
                            </div>
                        )}
                    </div>
                    {/* Heart animation overlay */}
                    {showHeartAnimation && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                            <Heart className="h-16 w-16 text-red-500 fill-current animate-ping" />
                        </div>
                    )}
                </div>
                <div className="flex gap-4 mt-1 ml-1 items-center">
                    <button
                        onClick={handleLike}
                        className={cn("text-xs font-medium flex items-center gap-1 hover:underline", isLiked ? "text-red-500" : "text-muted-foreground")}
                    >
                        <Heart className={cn("h-3 w-3", isLiked && "fill-current")} />
                        {likeCount > 0 && likeCount} Like
                    </button>
                    {canDelete && (
                        <button onClick={handleDelete} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1">
                            <Trash2 className="h-3 w-3" /> Obriši
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
